import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { MUTATE } from '@/store/modules/mutation_types';

const SPLIT_REGEX = /[,\t\n]/ug;

export default {
  data() {
    return {
      loadingBtn: false,
      isFormValid: false,
      accountId: this.dashboard?.accountId,
      universeId: this.dashboard?.universeId,
      newUsers: '',
      newFirstAccess: '',
      originId: '',
      environment: this.dashboard?.isStaging,
      existsOriginId: null,
      currentUsersList: [],
      users: this.dashboard?.users || [],
      firstAccessUser: this.dashboard?.users?.filter(({ admin }) => admin) || [],
      contractModel: Boolean(this.dashboard?.expiresTrialDate),
      expiresTrialDate: this.dashboard?.expiresTrialDate ? this.dashboard?.expiresTrialDate.toDate().toISOString().substr(0, 10) : null,
      isNumber: [
        v => !/\s/gu.test(v) || this.$t('FormModal.label.message.error.withoutSpace'),
        v => !isNaN(v) || this.$t('FormModal.label.message.error.mustBeNumber'),
        v => /^[0-9]{16}$/u.test(v) || this.$t('FormModal.label.message.error.invalidInfo'),
      ],
      existsAndIsNumber: [
        v => Boolean(v) || this.$t('FormModal.label.message.error.emptyField'),
        v => !/\s/gu.test(v) || this.$t('FormModal.label.message.error.withoutSpace'),
        v => !isNaN(v) || this.$t('FormModal.label.message.error.mustBeNumber'),
        v => /^[0-9]{16}$/u.test(v) || this.$t('FormModal.label.message.error.invalidInfo'),
      ],
      existsDashboard: [
        v => !/\s/gu.test(v) || this.$t('FormModal.label.message.error.withoutSpace'),
      ],
      emailRules: [
        v => (v && v.every(vv => Boolean(vv))) || this.$t('FormModal.label.message.error.addEmail'),
        v => (v && v.every(vv => /\S+@\S+\.\S+/u.test(vv))) || this.$t('FormModal.label.message.error.addAllEmails'),
      ],
      userEmailRules: [
        v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/u.test(v) || this.$t('FormModal.label.message.error.invalidEmail')
      ],
      contractModelRules: [
        () => Boolean(this.expiresTrialDate) || this.$t('FormModal.label.message.error.emptyField'),
      ],
      originIdErrors: [],
      maxCharacters: 16,
      displayAccHint: false,
      displayUniHint: false,
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'dashboards',
    ]),
    ...mapState('user', [
      'user'
    ]),
    isFormToEdit() {
      return this.dashboard !== null;
    },
    primaryButtonMessage() {
      return this.isFormToEdit ? this.$t('FormModal.label.button.save') : this.$t('FormModal.label.button.create');
    },
    isThereUsers() {
      return this.users ? this.users.length : false;
    },
    isThereFirstAccessUser() {
      return this.firstAccessUser ? this.firstAccessUser.length : false;
    },
    computedDateFormatted () {
      return this.expiresTrialDate !== null ? this.formatDate(this.expiresTrialDate) : null;
    },
  },
  watch: {
    originId(value) {
      this.dashboardExists(value).then(exists => {
        this.originIdErrors = exists ? [] : [this.$t('FormModal.label.message.error.invalidId')];
      });
    }
  },
  methods: {
    ...mapActions('dashboard', [
      'createDashboard',
      'editPermission',
      'getDashboardData',
      'getDashboard',
      'dashboardExists',
    ]),
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    validateAccMaxLength() {
      if (this.accountId.length > this.maxCharacters) {
        this.accountId = this.accountId.slice(0, this.maxCharacters);

        this.displayAccHint = true;
      }
    },
    validateUniMaxLength() {
      if (this.universeId.length > this.maxCharacters) {
        this.universeId = this.universeId.slice(0, this.maxCharacters);

        this.displayUniHint = true;
      }
    },
    removeUncopyableFiltersFromCards(cards) {
      const copyableFilters = [
        'genders',
        'polarities',
        'favorite',
        'dark_post',
        'sac_type',
        'sac_status',
        'sac_last_status',
      ];

      const newCards = cards?.map((card) => {
        const newCard = { ...card };

        if (card.props?.filter !== undefined) {
          const areThereFilters = Boolean(Object.keys(card.props?.filter).length === 0);

          if (!areThereFilters) {
            return card;
          }

          const filters = {};

          Object.keys(card.props?.filter).forEach(singleFilter => {
            const isThereFilterToCopy = copyableFilters.includes(singleFilter);

            if (isThereFilterToCopy) {
              filters[singleFilter] = newCard.props.filter[singleFilter];
            }
          });

          delete newCard.props.filter;
          newCard.props.filter = filters;
        }

        return newCard;
      });

      return newCards;
    },
    splitUsers() {
      const areThereMultipleUsers = this.newUsers && this.newUsers.split(SPLIT_REGEX).length > 1;

      if (areThereMultipleUsers) {
        let splittedUsers = this.newUsers.split(SPLIT_REGEX);
        splittedUsers = [...new Set(splittedUsers)].filter(value => String(value).trim());

        this.currentUsersList = [...new Set(this.currentUsersList.concat(splittedUsers))];

        this.users = this.stringArrayToObjectArray(this.currentUsersList);
        this.newUsers = '';
      }
    },
    splitFirstAccessUser() {
      const users = this.newFirstAccess?.split(SPLIT_REGEX) || [];

      if (users.length > 1) {
        this.firstAccessUser = this.firstAccessUser.length
          ? this.firstAccessUser
          : [{ email: users[0] }];
        this.newFirstAccess = '';
      }

      if (this.firstAccessUser?.length > 1) {
        this.firstAccessUser.pop();
      }
    },
    toDate(timestamp) {
      if (!timestamp) return null;

      const date = timestamp.toDate().toISOString();
      return this.formatDateWithTime(date);
    },
    formatDateWithTime(date) {
      const dateFormatted = this.formatDate(date.substr(0, 10));
      const [hour, min] = date.substr(11, date.length).split(':');

      return `${dateFormatted} ${hour}:${min}`;
    },
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },
    emailToUserObject(email, isAdmin = false, accessPage = false) {
      const isCreateByAccessPage = this.dashboard?.createdByAccessPage || accessPage;

      if (!email) return {};

      return {
        email: email.trim(),
        admin: isAdmin,
        onboardingStatus: {
          key: isCreateByAccessPage ? 'TO_DO' : 'SKIP',
          value: isCreateByAccessPage ? 'Não iniciado' : 'Pular',
        },
      };
    },
    stringArrayToObjectArray(array, isAdmin) {
      return array.map((string) => this.emailToUserObject(string, isAdmin));
    },
  }
};
